import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { CommonHeaderProps, EntryPoint } from 'corgi-types';
import type { BorderColorProps, BorderStyleProps, BorderWidthProps } from 'styled-system';
import { borderColor, borderStyle, borderWidth } from 'styled-system';

import Drawer from 'common/components/Drawer';
import { Flex } from 'common/components/Flex';
import Separator from 'common/components/Separator';
import { CenteredSpinner } from 'common/components/Spinner';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { EventBusEvent } from 'common/enums/EventBus';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useKeyboardClose } from 'common/hooks/keyboard';
import { useSubscription } from 'common/hooks/subscription';
import type {
  BorderLeftWidthProps,
  OverflowXProps,
  OverflowYProps,
} from 'common/theme/system-utilities';
import { borderLeftWidth, overflowX, overflowY } from 'common/theme/system-utilities';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import { SideNavType } from 'common/utils/navigation';
import BottomSection from './BottomSection';
import { useNavigation } from './hooks/useNavigation';
import { SCROLL_ID_PREFIX, useScrollHandling } from './hooks/useScrollHandling';
import { useSideNavKeyboardNavigation } from './hooks/useSideNavKeyboardNavigation';
import { useSideNavTabs } from './hooks/useSideNavTabs';
import NavigationList, { NAV_TITLE_ID } from './NavigationList';
import SideNavHeader from './SideNavHeader';
import {
  geSideNavDrawerWrapperStyles,
  getSideDrawerWidth,
  getSideNavDrawerContainerStyles,
} from './utils';

const SideNavContent = styled(Flex)<OverflowXProps>`
  ${overflowX};
`;

const sideNavContentStyles: ComponentProps<typeof SideNavContent> = {
  as: 'nav',
  overflowX: 'hidden',
  height: '100%',
};

const DrawerContainer = styled(Flex)<OverflowYProps & OverflowXProps>`
  ${overflowY};
  ${overflowX};
`;

const drawerContainerStyles: ComponentProps<typeof DrawerContainer> = {
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
  pl: [null, null, 'sp_20', 'sp_24'],
  pr: [null, null, 'sp_20', 'sp_24'],
  overflowX: 'hidden',
  minWidth: [null, null, 280, 320],
  maxWidth: [null, null, 280, 320],
};

const VerticalDivider = styled.div<
  BorderWidthProps & BorderLeftWidthProps & BorderStyleProps & BorderColorProps
>`
  ${borderWidth};
  ${borderLeftWidth};
  ${borderStyle};
  ${borderColor};
`;

const verticalDividerStyles: ComponentProps<typeof VerticalDivider> = {
  borderWidth: 1,
  borderLeftWidth: 0,
  borderStyle: 'solid',
  borderColor: 'sys.neutral.border.weak',
};

interface SideNavProps extends Pick<CommonHeaderProps, 'customer'> {
  navigation: GraphqlNavigationItemProps;
  uspEntryPoints?: EntryPoint[];
  logoUrl?: string;
  shopName?: string;
  shopUrl?: string;
  withBottomSection?: boolean;
  type?: SideNavType;
}

// eslint-disable-next-line max-lines-per-function
const SideNav = ({
  navigation: initialNavigation,
  logoUrl,
  shopName,
  shopUrl,
  withBottomSection = true,
  customer: initialCustomer,
  uspEntryPoints = [],
  type = SideNavType.Global,
}: SideNavProps) => {
  const withSideNavTabs = useFeatureFlagIsOn(GrowthbookFlag.SideNavTabs);

  const [customer] = useSubscription<CommonHeaderProps['customer']>(
    EventBusEvent.UserAccountUpdate,
    {
      latestEmitted: true,
      initialValue: initialCustomer,
    }
  );

  const {
    isOpen,
    currentNavigation,
    closeHandler,
    setCurrentNavigation,
    selectedItem,
    setSelectedItem,
    navigation,
    fetchedNavigation,
    tabs,
    clickHandler,
    isCascading,
    navItemParent,
    isLoading,
  } = useNavigation({ initialNavigation, type, withSideNavTabs });

  const {
    isTabsEnabled,
    isAtRootLevel,
    activeTabLabel,
    isDefaultTab,
    handleClose,
    TabBarComponent,
    desktopNavigation,
    desktopNavLength,
    mobileNavigation,
    headerNavigation,
    tabPanelProps,
    hideMobileTitle,
  } = useSideNavTabs({
    tabs,
    withSideNavTabs,
    type,
    isOpen,
    currentNavigation,
    selectedItem,
    navigation,
    fetchedNavigation,
    closeHandler,
    setCurrentNavigation,
    setSelectedItem,
  });

  useKeyboardClose(isOpen, handleClose);

  const { drawerRef, keyDownHandler } = useSideNavKeyboardNavigation({
    isOpen,
    isCascading,
    currentNavigation,
    setCurrentNavigation,
  });

  useScrollHandling(currentNavigation, isOpen);

  const isDesktop = Array.isArray(navigation);

  return (
    <div data-corgi-id="side-nav">
      <Drawer
        id="side-navigation"
        isOpen={isOpen}
        isSideNav
        width={getSideDrawerWidth(isOpen, isCascading, currentNavigation.length)}
        onBackdropClick={handleClose}
        ariaLabelledby={NAV_TITLE_ID}
        containerRef={drawerRef}
        onKeyDown={keyDownHandler}
      >
        {isOpen && (
          <>
            {isLoading && (currentNavigation.length > 1 || selectedItem) ? (
              <CenteredSpinner size={60} />
            ) : (
              <>
                <SideNavHeader
                  fetchedNavigation={headerNavigation}
                  onClickCloseButton={handleClose}
                  currentNavigation={currentNavigation}
                  setCurrentNavigation={setCurrentNavigation}
                  selectedItem={selectedItem}
                  setSelectedItem={setSelectedItem}
                  isCascading={isCascading}
                  logoUrl={logoUrl}
                  shopName={shopName}
                  shopUrl={shopUrl}
                  isLoading={isLoading}
                  isTabsEnabled={isTabsEnabled}
                />
                {isDesktop ? (
                  <Flex flexDirection="column" flex={1} minHeight={0}>
                    {isTabsEnabled && (
                      <>
                        <Flex
                          flexDirection="column"
                          flexShrink={0}
                          pl={[null, null, 'sp_20', 'sp_24']}
                          pr={[null, null, 'sp_20', 'sp_24']}
                          minWidth={[null, null, 280, 320]}
                          maxWidth={[null, null, 280, 320]}
                          {...getSideNavDrawerContainerStyles(0, desktopNavLength)}
                        >
                          {TabBarComponent}
                        </Flex>
                        <Separator mb="sp_24" mx={[null, null, 'sp_48', 'sp_64']} flexShrink={0} />
                      </>
                    )}
                    <SideNavContent
                      {...sideNavContentStyles}
                      {...tabPanelProps}
                      flex={1}
                      minHeight={0}
                    >
                      {(desktopNavigation as GraphqlNavigationItemProps[]).map((nav, level) => {
                        return (
                          <Flex
                            as="section"
                            key={level}
                            {...geSideNavDrawerWrapperStyles(level, selectedItem)}
                          >
                            <DrawerContainer
                              flex="1"
                              id={`${SCROLL_ID_PREFIX}-${level}`}
                              {...drawerContainerStyles}
                              {...getSideNavDrawerContainerStyles(level, desktopNavLength)}
                            >
                              <NavigationList
                                navigation={nav}
                                onNavigationClick={clickHandler(level)}
                                activeItem={currentNavigation[level]}
                                navLevel={level}
                                selectedItem={selectedItem}
                                shopName={shopName}
                                navItemParent={navItemParent}
                                isLoading={isLoading}
                                hideTitle={isTabsEnabled}
                                tabName={activeTabLabel}
                                isDefaultTab={isDefaultTab}
                              />
                              {withBottomSection && selectedItem === null && level === 0 && (
                                <BottomSection
                                  customer={customer}
                                  uspEntryPoints={uspEntryPoints}
                                  isExpanded={desktopNavLength > 1}
                                />
                              )}
                            </DrawerContainer>
                            {level < desktopNavLength - 1 && (
                              <VerticalDivider {...verticalDividerStyles} />
                            )}
                          </Flex>
                        );
                      })}
                    </SideNavContent>
                  </Flex>
                ) : (
                  <DrawerContainer as="nav" id={SCROLL_ID_PREFIX} {...drawerContainerStyles}>
                    {isAtRootLevel && TabBarComponent}
                    <div {...tabPanelProps}>
                      <NavigationList
                        navigation={mobileNavigation as GraphqlNavigationItemProps}
                        onNavigationClick={clickHandler(currentNavigation.length)}
                        navLevel={currentNavigation.length}
                        selectedItem={selectedItem}
                        shopName={shopName}
                        navItemParent={navItemParent}
                        isLoading={isLoading}
                        hideTitle={hideMobileTitle}
                        tabName={activeTabLabel}
                        isDefaultTab={isDefaultTab}
                      />
                    </div>
                    {withBottomSection && (
                      <BottomSection customer={customer} uspEntryPoints={uspEntryPoints} />
                    )}
                  </DrawerContainer>
                )}
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default SideNav;
